import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import {
  ConfigurationCreateInput,
  ConfigurationScope,
  ConfigurationValue,
  IConfiguration,
  IConfigurationItem,
} from '@wap/configuration';
import { mergeMap } from 'rxjs/operators';
import { User } from '@wam/shared';
import { isNil } from 'lodash-es';
import { WithAuthService } from '@app/shared/services/with-auth.service';
import { ApiGatewayServiceV2 } from '@app/authentication/api-gateway-v2/api-gateway-v2.service';

@Injectable({
  providedIn: 'root',
})
export class ConfigurationDataService extends WithAuthService {
  private readonly configurationApi = inject(ApiGatewayServiceV2).for('configuration');

  load(): Observable<IConfiguration> {
    return this.withUser().pipe(
      mergeMap((user: User) => {
        if (isNil(user)) {
          return of({});
        } else {
          return this.configurationApi.get<IConfiguration>(
            `v3/apps/${user?.application}/configurations`,
            {
              keyForAttribute: (key) => key,
              refreshOnExpiredToken: false,
            },
          );
        }
      }),
    );
  }

  save(
    key: string,
    value: ConfigurationValue,
    organization?: string,
    school?: string,
    scope?: ConfigurationScope,
  ): Observable<{ data: { attributes: IConfiguration } }> {
    return this.withUser().pipe(
      mergeMap((user: User) => {
        const body: ConfigurationCreateInput = {
          key,
          value,
          organization: organization ?? user.organization,
        };
        if (school) {
          body.school = school;
        }
        if (scope) {
          body.scope = scope;
        }
        return this.configurationApi.simplePost<{ data: { attributes: IConfiguration } }>(
          `v3/apps/${user?.application}/configurations`,
          body,
        );
      }),
    );
  }

  saveForApplication(
    application: string,
    key: string,
    value: ConfigurationValue,
    organization: string,
    school?: string,
    scope?: ConfigurationScope,
  ): Observable<{ data: { attributes: IConfiguration } }> {
    const body: ConfigurationCreateInput = {
      key,
      value,
      organization,
    };

    if (school) {
      body.school = school;
    }
    if (scope) {
      body.scope = scope;
    }

    return this.configurationApi.simplePost<{ data: { attributes: IConfiguration } }>(
      `v3/apps/${application}/configurations`,
      body,
    );
  }

  saveValue(
    key: string,
    value: ConfigurationValue,
  ): Observable<{ data: { attributes: IConfiguration } }> {
    return this.withUser().pipe(
      mergeMap((user: User) => {
        const body = {
          key,
          value,
          organization: user.organization,
        };
        return this.configurationApi.simplePost<{ data: { attributes: IConfiguration } }>(
          `v3/apps/${user?.application}/configurations`,
          body,
        );
      }),
    );
  }

  getGlobal(key: string): Observable<IConfigurationItem> {
    return this.withUser().pipe(
      mergeMap((user: User) =>
        this.configurationApi.get<IConfigurationItem>(`v3/apps/global/configurations/find/${key}`),
      ),
    );
  }

  get(key: string): Observable<IConfigurationItem> {
    return this.withUser().pipe(
      mergeMap((user: User) =>
        this.configurationApi.get<IConfigurationItem>(
          `v3/apps/${user?.application}/configurations/find/${key}`,
        ),
      ),
    );
  }

  findBy(application: string, key: string): Observable<IConfigurationItem> {
    return this.configurationApi.get<IConfigurationItem>(
      `v3/apps/${application}/configurations/find/${key}`,
    );
  }

  savePrivate(
    key: string,
    value: ConfigurationValue,
  ): Observable<{ data: { attributes: IConfiguration } }> {
    return this.withUser().pipe(
      mergeMap((user: User) => {
        const organization = user.organization ?? this.getDefaultOrganization(user.application);
        const userId = user.uuid ?? user.sub;
        const body: ConfigurationCreateInput = {
          key,
          value,
          scope: ConfigurationScope.PRIVATE,
        };
        return this.configurationApi.simplePut<{ data: { attributes: IConfiguration } }>(
          `v3/apps/${user.application}/orgs/${organization}/users/${userId}/configurations`,
          body,
        );
      }),
    );
  }

  findForOrganization(
    application: string,
    organization: string,
    key: string,
  ): Observable<IConfigurationItem> {
    return this.configurationApi.get<IConfigurationItem>(
      `v3/apps/${application}/orgs/${organization}/configurations/find/${key}`,
    );
  }

  private getDefaultOrganization(application: string): string | undefined {
    switch (application) {
      case 'waterford-upstart':
        return 'upstart';
      case 'waterford-parent':
        return 'mentor';
      case 'waterford-admin':
        return 'admin';
      default:
        return undefined;
    }
  }
}
